import { Dispatch, FC, SetStateAction, useEffect, useRef, useState } from 'react';
import { generalBlue, generalRed } from 'src/app-config';
import { HTHPolygonInfoBox } from 'src/components/_commons/house-to-house';
import { Polygon } from '@react-google-maps/api';
import { typeBlock, typeFace, typeHTHTerritory, typeHTHFace, typeTerritoryNumber } from '@monorepo/models';
import { useGetConfig } from 'src/custom-hooks';

type propsType = {
    currentFace: typeHTHFace | null;
    hthTerritory: typeHTHTerritory;
    isAddingPolygon: boolean;
    isEditingView: boolean;
    polygon: typeHTHFace;
    runIntervals: boolean;
    selectBlockAndFaceHandler: (block?: typeBlock, face?: typeFace, hthTerritory0?: typeHTHTerritory | null) => void;
    setHTHTerritory: Dispatch<SetStateAction<typeHTHTerritory | null>>;
    territoryNumber: typeTerritoryNumber;
};

export const HTHSectionMapPolygon: FC<propsType> = ({
    currentFace, hthTerritory, isAddingPolygon, isEditingView, polygon, runIntervals,
    selectBlockAndFaceHandler, setHTHTerritory, territoryNumber
}) => {
    const [polygonColor, setPolygonColor] = useState(polygon.color ?? generalBlue);
    const [showInfoWindow, setShowInfoWindow] = useState(false);
    const config = useGetConfig();
    const ref = useRef<google.maps.Polygon>(null);

    useEffect(() => {
        setPolygonColor(polygon.id === currentFace?.id ? generalRed : polygon.color ?? generalBlue);
    }, [currentFace, polygon.color, polygon.serviceDates, polygon.id]);

    useEffect(() => {
        const _interval = setInterval(() => {
            if (!ref.current) {
                return;
            }
            const path = ref.current.getPath().getArray();
            const p1y = path[0].lat();
            const p1x = path[0].lng();
            const p2y = path[1].lat();
            const p2x = path[1].lng();
            const p3y = path[2].lat();
            const p3x = path[2].lng();
            const p4y = path[3]?.lat() ?? 0;
            const p4x = path[3]?.lng() ?? 0;
            if (p4y && p4x) {
                if (polygon && p1y === polygon.coordsPoint1.lat && p1x === polygon.coordsPoint1.lng
                    && p2y === polygon.coordsPoint2.lat && p2x === polygon.coordsPoint2.lng
                    && p3y === polygon.coordsPoint3.lat && p3x === polygon.coordsPoint3.lng
                    && p4y === polygon.coordsPoint4?.lat && p4x === polygon.coordsPoint4?.lng
                ) {
                    return;
                }
            } else {
                if (polygon && p1y === polygon.coordsPoint1.lat && p1x === polygon.coordsPoint1.lng
                    && p2y === polygon.coordsPoint2.lat && p2x === polygon.coordsPoint2.lng
                    && p3y === polygon.coordsPoint3.lat && p3x === polygon.coordsPoint3.lng
                ) {
                    return;
                }
            }
            const modifiedPolygon: typeHTHFace = {
                block: polygon.block,
                buildings: [],
                coordsPoint1: { lat: p1y, lng: p1x },
                coordsPoint2: { lat: p2y, lng: p2x },
                coordsPoint3: { lat: p3y, lng: p3x },
                dateOfLastSharing: null,
                doNotCalls: polygon.doNotCalls,
                face: polygon.face,
                id: polygon.id,
                observations: polygon.observations,
                serviceDates: polygon.serviceDates,
                street: polygon.street
            };
            const currentHTHTerritory = { ...hthTerritory };
            if (currentHTHTerritory.newBlockPolygon && p4x && p4y) {
                modifiedPolygon.coordsPoint4 = { lat: p4y, lng: p4x };
                currentHTHTerritory.newBlockPolygon.coordinates = [
                    { lat: p1y, lng: p1x },
                    { lat: p2y, lng: p2x },
                    { lat: p3y, lng: p3x },
                    { lat: p4y, lng: p4x }
                ];
            } else {
                currentHTHTerritory.faces = currentHTHTerritory.faces.map(p =>
                    p.id === polygon.id ? modifiedPolygon : p
                );
            }
            setHTHTerritory(currentHTHTerritory);
        }, 500);
        if (!runIntervals) {
            clearInterval(_interval);
        }
        return () => clearInterval(_interval);
    }, [hthTerritory, polygon, runIntervals, setHTHTerritory]);

    return (
        <>
        <Polygon
            editable={isEditingView || (isAddingPolygon && polygon.id === 0)}
            draggable={isEditingView || (isAddingPolygon && polygon.id === 0)}
            onClick={() => {
                if (isEditingView || isAddingPolygon) {
                    return;
                }
                selectBlockAndFaceHandler(polygon.block, polygon.face);
            }}
            onLoad={googlePolygon => {
                ref.current = googlePolygon;
            }}
            onMouseOver={() => {
                if (isEditingView || isAddingPolygon || !ref.current || currentFace) {
                    return;
                }
                setPolygonColor(polygon.color ?? generalRed);
                setShowInfoWindow(true);
            }}
            onMouseOut={() => {
                if (isEditingView || isAddingPolygon || !ref.current || currentFace) {
                    return;
                }
                setPolygonColor(polygon.color ?? generalBlue);
                setShowInfoWindow(false);
            }}
            options={{
                clickable: true,
                fillColor: polygonColor,
                fillOpacity: currentFace && currentFace.id === polygon.id ? 1 : 0.92,
                strokeColor: '',
                strokeOpacity: 0.9,
                strokePosition: google.maps.StrokePosition.CENTER,
                strokeWeight: 10
            }}
            path={[
                polygon.coordsPoint1,
                polygon.coordsPoint2,
                polygon.coordsPoint3,
                ...(polygon.coordsPoint4 ? [polygon.coordsPoint4] : [])
            ]}
        />
        {!!polygon.id && (showInfoWindow || (currentFace && currentFace.id === polygon.id)) &&
            <HTHPolygonInfoBox
                onClickHandler={() => {
                    if (isEditingView || isAddingPolygon) {
                        return;
                    }
                    selectBlockAndFaceHandler(polygon.block, polygon.face);
                }}
                polygon={polygon}
                territoryNumber={territoryNumber}
                usingLettersForBlocks={config.isUsingLettersForBlocks}
            />
        }
        </>
    );
}
